// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import FancyScroll from "@eisinc/App/Components/FancyScroll/FancyScroll";
import SalesRepComponent from "@eisinc/App/Components/SalesRep/SalesRep";
import LazyImage, { LazyImageProps } from "@eisinc/MobiusOverrides/LazyImage";
import ChevronRight from "@eisinc/XDL/Icons/ChevronRight";
import { xScreen } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLink, getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

export interface MyAccountQuickLinkModel {
    fields: {
        imageUrl: string;
        title: string;
        linkText: string;
        link: LinkFieldValue;
    };
    link: any;
}

const enum fields {
    links = "links",
}

interface MyAccountQuickLinksStyles {
    widgetContainer?: InjectableCss;
    linksContainer?: GridContainerProps;
    linkGridItem?: GridItemProps;
    link?: LinkPresentationProps;
    linkContentContainer?: InjectableCss;
    image?: LazyImageProps;
    titleTextStyles?: InjectableCss;
    titleStyles?: TypographyPresentationProps;
    linkTextContainer?: InjectableCss;
    linkTextStyles?: TypographyPresentationProps;
    chevronRightIconStyle?: IconPresentationProps;
    salesRepContainer?: InjectableCss;
    salesRepContainerHeader?: InjectableCss;
    salesRepBodyContainer?: InjectableCss;
    welcomeStyles?: TypographyPresentationProps;
    salesRepSubTitleStyles?: TypographyPresentationProps;
}

const myAccountQuickLinksStyles: MyAccountQuickLinksStyles = {
    widgetContainer: {
        css: css`
            border: 1px solid #dfe3e6;
            background-color: #ffffff;
            box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.1);
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            justify-content: center;
            max-width: 1180px;
            margin: 0 auto;

            ${xScreen("lte", "md")} {
                max-width: 100%;
                max-height: 100%;
                width: 100%;
            }
        `,
    },
    salesRepContainer: {
        css: css`
            width: 25%;
            min-height: 210px;
            max-height: 218px;
            display: flex;
            flex-direction: column;
            box-sizing: border-box;
            border-right: 1px solid #dfe3e6;
            box-shadow: 1px 0 1px 0 rgba(0, 0, 0, 0.1);
            padding: 15px 35px 15px 15px;
            overflow: hidden;

            ${xScreen("lte", "md")} {
                width: 50%;
                max-height: 100%;
            }

            ${xScreen("lte", "sm")} {
                width: 100%;
                border: 0;
            }
        `,
    },
    salesRepContainerHeader: {
        css: css`
            display: flex;
            flex-direction: column;
            flex-wrap: nowrap;
        `,
    },
    salesRepBodyContainer: {
        css: css`
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
        `,
    },
    linksContainer: {
        css: css`
            padding: 40px;
            width: 75%;

            ${xScreen("lte", "md")} {
                width: 50%;
            }

            ${xScreen("lte", "sm")} {
                width: 100%;
            }
        `,
    },
    linkGridItem: {
        css: css`
            justify-content: center;
            box-shadow: 1px 0 1px 0 transparent;
            border: 1px solid transparent;
            align-items: flex-end;

            &:hover {
                box-shadow: 1px 0 1px 0 rgba(0, 0, 0, 0.1);
                border-color: #dfe3e6;
            }

            ${xScreen("lte", "xs")} {
                justify-content: flex-start;
                width: 100%;
            }
        `,
    },
    link: {
        css: css`
            margin: 0 10px;

            &&:hover {
                text-decoration: none;
                cursor: pointer;

                * {
                    text-decoration: none;
                }
            }

            ${xScreen("lte", "xs")} {
                &,
                > * {
                    width: 100%;
                }
            }
        `,
    },
    linkContentContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            width: 100%;
        `,
    },
    image: {
        css: css`
            width: 70px;
            height: 70px;
            display: flex;
            justify-content: center;
            align-items: center;
            img {
                width: auto;
                height: auto;
                max-width: 100%;
                max-height: 100%;
            }
        `,
    },
    titleTextStyles: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    titleStyles: {
        variant: "h4",
        forwardAs: "span",
        css: css`
            max-width: 153px;
            color: ${getColor("text.main")};
            letter-spacing: 0;
            text-align: center;
        `,
    },
    welcomeStyles: {
        css: css`
            color: ${getColor("text.main")};
            font-size: 21px;
            letter-spacing: 0;
            line-height: 25px;
        `,
    },
    salesRepSubTitleStyles: {
        css: css`
            color: ${getColor("text.main")};
            font-size: 14px;
            letter-spacing: 0;
            line-height: 20px;
            margin-bottom: 12px;
        `,
    },
    linkTextContainer: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
        `,
    },
    linkTextStyles: {
        css: css`
            color: ${getColor("primary.main")};
            font-size: 16px;
            letter-spacing: 0;
            line-height: 30px;
            margin-right: 5px;
            text-decoration: none;
        `,
    },
    chevronRightIconStyle: {
        css: css`
            g {
                fill: ${getColor("primary.main")};
            }
        `,
    },
};

interface MyAccountQuickLinksProps extends WidgetProps {
    [fields.links]: MyAccountQuickLinkModel[];
}

const mapStateToProps = (state: ApplicationState, myAccountQuickLinksProps: MyAccountQuickLinksProps) => {
    if (myAccountQuickLinksProps.fields.links) {
        return {
            processedLinks: myAccountQuickLinksProps.fields[fields.links]
                .map((link: MyAccountQuickLinkModel) => {
                    return {
                        ...link,
                        link: getLink(state, link.fields.link),
                    };
                })
                .filter((link: any) => {
                    return !link?.link?.excludeFromNavigation;
                }),
            session: getSession(state),
        };
    }
    return {
        processedLinks: [],
        session: getSession(state),
    };
};

type Props = MyAccountQuickLinksProps & ReturnType<typeof mapStateToProps>;

const styles = myAccountQuickLinksStyles;
// EIS-4192 - replace div with semantic HTML elements
const SectionWrapper = styled.section`
    ${styles.widgetContainer?.css}
`;

const MyAccountQuickLinksTemplateWidget: React.FC<Props> = (props: Props) => {
    const secondarySalesRep =
        props.session.properties["secondarySalesRep"] && JSON.parse(props.session.properties["secondarySalesRep"]);
    const primarySalesRep =
        props.session.properties["primarySalesRep"] && JSON.parse(props.session.properties["primarySalesRep"]);
    const { session } = props;

    return (
        <SectionWrapper>
            <StyledWrapper {...styles.salesRepContainer}>
                <FancyScroll showOverlay={true}>
                    <StyledWrapper {...styles.salesRepContainerHeader}>
                        <Typography {...styles.welcomeStyles} as="h1">
                            {`${translate("Welcome ") + (session.firstName || session.userName)}!`}
                        </Typography>
                        <Typography {...styles.salesRepSubTitleStyles} as="h2">
                            {translate("Get in touch with your EIS rep(s):")}
                        </Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.salesRepBodyContainer}>
                        {!primarySalesRep && !secondarySalesRep ? (
                            siteMessage("Custom_No_Sales_Rep", "No sales rep for this account")
                        ) : (
                            <SalesRepComponent smallImageSize={true} />
                        )}
                    </StyledWrapper>
                </FancyScroll>
            </StyledWrapper>
            <GridContainer
                {...styles.linksContainer}
                offsetProps={{
                    style: { justifyContent: "space-around" },
                }}
            >
                {props.processedLinks &&
                    props.processedLinks.map((link: MyAccountQuickLinkModel) => {
                        return (
                            <GridItem width={[12, 6, 6, 3, 3]} key={link.fields.link.value} {...styles.linkGridItem}>
                                <Link href={link?.link?.url} aria-label={link.fields.linkText} {...styles.link}>
                                    <StyledWrapper {...styles.linkContentContainer}>
                                        {link.fields.imageUrl && (
                                            <LazyImage
                                                {...styles.image}
                                                src={link.fields.imageUrl}
                                                altText={link.fields.linkText}
                                            />
                                        )}

                                        <StyledWrapper {...styles.titleTextStyles}>
                                            <Typography {...styles.titleStyles}>{link.fields.title}</Typography>
                                            {link.fields.linkText && (
                                                <StyledWrapper {...styles.linkTextContainer}>
                                                    <Typography {...styles.linkTextStyles}>
                                                        {link.fields.linkText}
                                                    </Typography>
                                                    <Icon
                                                        src={ChevronRight}
                                                        {...styles.chevronRightIconStyle}
                                                        size={12}
                                                    />
                                                </StyledWrapper>
                                            )}
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </Link>
                            </GridItem>
                        );
                    })}
            </GridContainer>
        </SectionWrapper>
    );
};

export const definition: WidgetDefinition = {
    group: "Basic",
    fieldDefinitions: [
        {
            name: fields.links,
            editorTemplate: "ListField",
            getDisplay: (item: HasFields, state) => {
                const { type, value } = item.fields.link;
                if (type === "Page") {
                    const link = getPageLinkByNodeId(state, value);
                    return link?.title;
                }
                if (type === "Category") {
                    return value; // TODO ISC-10781 make this work
                }
                if (type === "Url") {
                    return value;
                }
                return value;
            },
            defaultValue: [],
            fieldType: "General",
            fieldDefinitions: [
                {
                    name: "link",
                    editorTemplate: "CustomLinkField" as any,
                    defaultValue: {
                        value: "",
                        type: "Page",
                    },
                    isRequired: true,
                },
                {
                    name: "imageUrl",
                    displayName: "Image",
                    editorTemplate: "ImagePickerField",
                    defaultValue: "",
                },
                {
                    name: "title",
                    displayName: "Title",
                    editorTemplate: "TextField",
                    defaultValue: "",
                },
                {
                    name: "linkText",
                    displayName: "Link Text",
                    editorTemplate: "TextField",
                    defaultValue: "",
                },
            ],
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(MyAccountQuickLinksTemplateWidget),
    definition,
};

export default widgetModule;
